import { useState } from "react";
import { useUserContext } from "../../context/UserContext";
import { toast } from "react-toastify";
import { apiRequest } from "@/network/apiClient";
import { API_ENDPOINTS } from "@/api/apiEndpoints";

// Enum mappings: Display label -> Backend enum value
const REGION_ENUMS = {
  "Malaysia": "Malaysia",
  "Singapore": "Singapore",
  "Brunei": "Brunei"
};

const LEAD_SOURCE_ENUMS = {
  "Self-Generated": "Self-Generated",
  "Bytedance": "Bytedance",
  "Inbound": "Inbound",
  "Webscraping": "Webscraping"
};

const INTEREST_LEVEL_ENUMS = {
  "Hot": "Hot",
  "Warm": "Warm",
  "Cold": "Cold" // TODO: change to enum value on next phase
};

// Display labels for interest levels (with emojis)
const INTEREST_LEVEL_DISPLAY = {
  "Hot": "🔥 Hot",
  "Warm": "⚡ Warm",
  "Cold": "❄️ Cold"
};

const AddNewLead = ({isOpen, onClose}) => {
  if (!isOpen) return null

  const [step, setStep] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errors, setErrors] = useState({});
  const { userId } = useUserContext();
  const [formData, setFormData] = useState({
    region: "Malaysia",
    companyName: "",
    leadSource: "Self-Generated",
    interestLevel: "Hot",
    products: { Remittance: true, Insurance: false, Financing: false },
    set_reminder: "no",
    reminder_title: "",
    reminder_datetime: "",
    reminder_details: ""
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Clear error for the field being changed
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        products: { ...prev.products, [name]: checked },
      }));
    } else if (type === "radio") {
      // If set_reminder is changed to "no", clear reminder fields
      if (name === "set_reminder" && value === "no") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          reminder_title: "",
          reminder_datetime: "",
          reminder_details: ""
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleContinue = () => {
    // Validate company name is filled
    if (!formData.companyName || formData.companyName.trim() === "") {
      setErrors({ companyName: "Company Name is required" });
      return;
    }

    // Clear any errors if validation passes
    setErrors({});

    const selectedProducts = Object.values(formData.products).filter(Boolean).length;
    
    if (selectedProducts > 1) {
      // Show custom modal instead of window.confirm
      setShowConfirmModal(true);
    } else {
      // Proceed directly if only one product is selected
      setStep(2);
    }
  };

  // Get current datetime in format required by datetime-local input (YYYY-MM-DDTHH:mm)
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Transform form data to send enum values to backend
  const transformFormDataForBackend = (data) => {
    const transformed = {
      region: REGION_ENUMS[data.region] || data.region,
      companyName: data.companyName,
      leadSource: LEAD_SOURCE_ENUMS[data.leadSource] || data.leadSource,
      interestLevel: INTEREST_LEVEL_ENUMS[data.interestLevel] || data.interestLevel,
      products: data.products,
      set_reminder: data.set_reminder === "yes", // Convert to boolean
    };

    // Only include reminder fields if set_reminder is true
    if (data.set_reminder === "yes") {
      transformed.reminder_title = data.reminder_title;
      transformed.reminder_datetime = data.reminder_datetime;
      transformed.reminder_details = data.reminder_details;
    }

    return transformed;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Transform display values to enum values before sending
      const transformedData = transformFormDataForBackend(formData);
      const result = await apiRequest(`${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.UPDATE_LEADS.ADD_LEAD}`, 'POST', transformedData)
      toast.success('Lead added successfully!');
      onClose();
    } catch (error) {
      // Extract error message from backend response
      let errorMessage = 'Unable to add lead';
      
      if (error?.response?.data) {
        // Try different possible error message formats
        const errorData = error.response.data;
        errorMessage = 
          errorData.message || 
          errorData.error || 
          errorData.detail ||
          (typeof errorData === 'string' ? errorData : JSON.stringify(errorData)) ||
          errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };
  

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div onClick={(e) => e.stopPropagation()} className="max-w-lg mx-auto bg-white p-6 rounded-md">
        <h2 className="text-xl font-bold mb-4">Add New Lead</h2>

        {/* Tabs */}
        <div className="flex border-b mb-4">
          <button
            className={`flex-1 pb-2 border-b-2 font-semibold ${
              step === 1 ? "border-yellow-400" : "text-gray-500"
            }`}
          >
            Company Details
          </button>
          <button
            className={`flex-1 pb-2 border-b-2 font-semibold ${
              step === 2 ? "border-yellow-400" : "text-gray-500"
            }`}
          >
            Set Reminder
          </button>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-auto">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Multiple Products Selected</h3>
              <p className="text-gray-500 mb-4">Are you sure you want to select multiple products?</p>
              <div className="flex justify-end space-x-3">
                <button 
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-800 transition-colors"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-yellow-400 hover:bg-yellow-500 rounded-md text-white transition-colors"
                  onClick={() => {
                    // Validate company name before proceeding
                    if (!formData.companyName || formData.companyName.trim() === "") {
                      setErrors({ companyName: "Company Name is required" });
                      setShowConfirmModal(false);
                      return;
                    }
                    setErrors({});
                    setShowConfirmModal(false);
                    setStep(2);
                  }}
                >
                  Proceeds
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              {/* Company Details */}
              <label className="block text-sm font-medium">Region</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="w-full p-2 border rounded mt-1 bg-gray-100"
              >
                {Object.keys(REGION_ENUMS).map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>

              <div className="mt-4 mb-6">
                  <label className="block text-base font-extrabold mb-2 mt-8">Company Details</label>
                  <label className="block text-sm font-medium">Company Name</label>
                  <input
                      type="text"
                      name="companyName"
                      placeholder="Enter Company Name"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded mt-1 bg-gray-100 ${
                        errors.companyName ? "border-red-500" : ""
                      }`}
                      required
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                  )}
              </div>

              {/* Lead Source & Interest Level */}
              <div className="mt-4 flex gap-4">
                  <label className="block text-sm font-medium">Lead Source</label>
                  <select
                    name="leadSource"
                    value={formData.leadSource}
                    onChange={handleChange}
                    className="w-full p-2 border rounded mt-1 bg-gray-100"
                  >
                    {Object.keys(LEAD_SOURCE_ENUMS).map((source) => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </select>
                  <label className="block text-sm font-medium">Interest Level</label>
                  <select
                    name="interestLevel"
                    value={formData.interestLevel}
                    onChange={handleChange}
                    className="w-full p-2 border rounded mt-1 bg-gray-100"
                  >
                    <option value="Hot">🔥 Hot</option>
                    <option value="Warm">⚡ Warm</option>
                    <option value="Cold">❄️ Cold</option>
                  </select>
              </div>

              {/* Products Associated */}
              <div className="mt-4">
                <label className="block text-sm font-medium">Products Associated</label>
                <div className="flex flex-col space-y-2 mt-1">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="Remittance"
                      checked={formData.products.Remittance}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Remittance
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="Insurance"
                      checked={formData.products.Insurance}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Insurance
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="Financing"
                      checked={formData.products.Financing}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Financing
                  </label>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="mt-6 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-md transition-colors"
                >
                  Continue
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Set Reminder Toggle */}
              <div className="mt-4 mb-6">
                <label className="block text-base font-extrabold mb-2">Set Reminder</label>
                <label className="block text-sm font-medium mb-2">Do you wish to set reminder?</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="set_reminder"
                      value="yes"
                      checked={formData.set_reminder === "yes"}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="set_reminder"
                      value="no"
                      checked={formData.set_reminder === "no"}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Reminder Fields - Only enabled when set_reminder is "yes" */}
              <div className="mt-4 mb-6">
                  <label className="block text-sm font-medium">Title / item</label>
                  <input
                      type="text"
                      name="reminder_title"
                      placeholder="Enter title"
                      value={formData.reminder_title}
                      onChange={handleChange}
                      disabled={formData.set_reminder === "no"}
                      required={formData.set_reminder === "yes"}
                      className={`w-full p-2 border rounded mt-1 ${
                        formData.set_reminder === "no" 
                          ? "bg-gray-200 cursor-not-allowed" 
                          : "bg-gray-100"
                      }`}
                />
              </div>

              <div className="mt-4 mb-6">
                <label className="block text-sm font-medium">Set Reminder Date</label>
                <input
                type="datetime-local"
                name="reminder_datetime"
                value={formData.reminder_datetime}
                onChange={handleChange}
                min={getCurrentDateTime()}
                disabled={formData.set_reminder === "no"}
                required={formData.set_reminder === "yes"}
                className={`w-full p-2 border rounded mt-1 ${
                  formData.set_reminder === "no" 
                    ? "bg-gray-200 cursor-not-allowed" 
                    : "bg-gray-100"
                }`}
                  />
              </div>

              <div className="mt-4 mb-6">
                  <label className="block text-sm font-medium">Details</label>
                  <textarea
                      name="reminder_details"
                      placeholder="Enter reminder details"
                      value={formData.reminder_details}
                      onChange={handleChange}
                      disabled={formData.set_reminder === "no"}
                      required={formData.set_reminder === "yes"}
                      className={`w-full p-2 border rounded mt-1 ${
                        formData.set_reminder === "no" 
                          ? "bg-gray-200 cursor-not-allowed" 
                          : "bg-gray-100"
                      }`}
                />
              </div>

              {/* Submit Button */}
              <div className="mt-6 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-md transition-colors"
                >
                  Create Lead
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddNewLead;